import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import { useSelector, useDispatch } from "react-redux";
import { updateModalManager } from "../../reducers/configurator/configuratorSlice";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { resetLayers } from "../../reducers/configurator/utils";
import { Check } from "@mui/icons-material";
import GltfLoader from "./configuratorForms/GltfLoader";
import useThreeJsLoader from "../../zustand/stores/useThreeJsLoader";
import GuestDetails from "./configuratorForms/GuestDetails";
import ScreenForm from "./configuratorForms/ScreenForm";
import FrameForm from "./configuratorForms/FrameForm";

const PREFIX = "ConfiguratorModalManager";

const classes = {
  scrollBarStyle: `${PREFIX}-scrollBarStyle`,
  dialogBackground: `${PREFIX}-dialogBackground`,
  buttonSpaces: `${PREFIX}-buttonSpaces`,
};

const ConfiguratorModalManagerContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.scrollBarStyle}`]: {
      "& > div > div::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "& > div > div::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "& > div > div::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.dialogBackground}`]: {
      backgroundColor: "#424242",
    },
    [`& .${classes.buttonSpaces}`]: {
      backgroundColor: "#424242",
      color: theme.palette.primary.main,
      marginRight: "15px",
      "&:hover": {
        backgroundColor: "rgba(66,66,66,0.7)",
        color: theme.palette.primary.main,
      },
    },
  };
});

const ConfiguratorModalManager = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const modalManager = useSelector((state) => state.configurator.modalManager);
  const layout = useSelector((state) => state.configurator.layout);
  const is3dView = useSelector((state) => state.configurator?.is3dView);

  const setReinitialize = useThreeJsLoader((state) => state.setReinitialize);

  const handleCloseModal = () => {
    if (!is3dView) setReinitialize();
    dispatch(updateModalManager(resetLayers.modalManager));
  };

  const forms = {
    custom: (
      <GltfLoader
        onlyModel={modalManager.mode === "onlyModel"}
        onlyInfos={modalManager.mode === "onlyInfos"}
        displayAll={modalManager.mode === "displayAll"}
        modifyElement={modalManager.renderer === "modifyElement"}
        gltf={modalManager.renderer === "gltf"}
      />
    ),
    guest: <GuestDetails />,
    screen: <ScreenForm layer={modalManager.layer} />,
    ipad: <ScreenForm layer={modalManager.layer} />,
  };

  return (
    <ConfiguratorModalManagerContainer>
      {modalManager.type === "dialog" ? (
        <ResponsiveDialogManager
          title={modalManager.title}
          disableGutters={modalManager.disableGutters}
          fullScreen
          open={modalManager.open}
          onClose={() => handleCloseModal()}
          renderer={
            typeof modalManager.renderer === "string" ? (
              forms[modalManager.renderer]
            ) : (
              <FrameForm layer={modalManager.orientation} />
            )
          }
          toolbarActions={
            <ConfiguratorModalManagerContainer>
              <IconButton
                className={classes.buttonSpaces}
                disabled={modalManager.disableSave}
                onClick={() => {
                  dispatch(
                    updateModalManager({ saveTrigger: true, lockAction: true })
                  );
                }}
              >
                {modalManager.lockAction ? (
                  <CircularProgress size={28} />
                ) : (
                  <Check />
                )}
              </IconButton>
            </ConfiguratorModalManagerContainer>
          }
        />
      ) : modalManager.type === "backdrop" ? (
        <Dialog
          aria-labelledby="simple-dialog-title"
          open={modalManager.open}
          onClose={() => handleCloseModal()}
          className={classes.scrollBarStyle}
        >
          <DialogTitle
            id="simple-dialog-title"
            className={classes.dialogBackground}
          >
            {modalManager.title ? (
              <Typography variant="body1">{modalManager.title}</Typography>
            ) : layout === "event" ? (
              <Typography variant="body1">
                {t("configurator.choseEventObject")}
              </Typography>
            ) : layout === "theme" ? (
              <Typography variant="body1">
                {t("configurator.choseThemeObject")}
              </Typography>
            ) : (
              <Typography variant="body1">
                {t("configurator.choseObject")}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>{modalManager.renderer}</DialogContent>
        </Dialog>
      ) : null}
    </ConfiguratorModalManagerContainer>
  );
};

export default ConfiguratorModalManager;
